/**
 * @file 搜索框
 * @author FengGuang(fengguang01@baidu.com)
 */

import React, { FC, isValidElement, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useLatest } from 'react-use';
import { AutoComplete, AutoCompleteProps, Input } from 'antd';
import { SearchOutlined, LoadingOutlined } from '@ant-design/icons'

import A from '../a/A';

import {
    useHotKeyWords,
    useRecentSearchWords,
    useSearchSug,
    useValue,
} from './searchACHooks';

import { ISearchACProps } from './types';

const SearchAC: FC<ISearchACProps> = (props) => {
    const {
        getPopupContainer
    } = props;

    const [open, setOpen] = useState<boolean>(false);

    const { hotKeyWords } = useHotKeyWords();
    const { recentSearchWords, addRecentSearchWords } = useRecentSearchWords();

    const { searchSug, loading, onSearchChange, submitSearch } = useSearchSug();

    const { value, setValue } = useValue();
    const valueRef = useLatest(value);

    const handleValue = (value: string): string => {
        if (value.includes('-search-result-handle-')) {
            return value.split('-search-result-handle-')[0];
        }
        return value;
    }


    const onChange = useCallback<NonNullable<AutoCompleteProps['onChange']>>((value) => {
        setValue(handleValue(value));
        onSearchChange(handleValue(value));
    }, [setValue, onSearchChange]);


    const onSelect = useCallback<NonNullable<AutoCompleteProps['onSelect']>>((value, option) => {
        if (value) {
            addRecentSearchWords(handleValue(value));
            submitSearch(handleValue(value), option);
        }
    }, [addRecentSearchWords, submitSearch]);


    const onOpen = useCallback((open) => {
        if (open) {
            onSearchChange(valueRef.current);
        }
        setOpen(open);
    }, [onSearchChange, valueRef]);


    const handleOnSubmitSearch = useCallback(() => {
        addRecentSearchWords(value);
        submitSearch(valueRef.current);
    }, [addRecentSearchWords, submitSearch, value, valueRef]);


    const valueIsNull = !value;
    const options = useMemo(() => {
        if (valueIsNull) {
            let i = 0;
            const res = [
                {
                    label: '热门搜索',
                    options: hotKeyWords.map((str) => ({
                        key: `${str},${i++}`,
                        label: str,
                        value: str
                    }))
                }
            ];
            if (recentSearchWords.length > 0) {
                res.unshift({
                    label: '最近搜索',
                    options: recentSearchWords.map((str) => ({
                        key: `${str},${i++}`,
                        label: str,
                        value: str
                    }))
                });
            }

            return res;
        }

        if (loading) {
            return [{
                label: '',
                value: ''
            }];
        }

        return searchSug.map((group) => {
            const theOpotions: any[] = group.options;
            return {
                ...group,
                options: theOpotions.map((item) => {
                    return {
                        ...item,
                        label: (
                            <A className="paddle-head-search-ac-overlay-item">
                                <span
                                    className="paddle-head-search-ac-overlay-item-title"
                                    {...{ dangerouslySetInnerHTML: { __html: item.label } }}
                                >
                                </span>
                                {!!item.type
                                    && (<>
                                        <div className="paddle-head-search-ac-overlay-item-divider" />
                                        <span className="paddle-head-search-ac-overlay-item-type">
                                            {item.type}
                                        </span>
                                    </>)
                                }
                            </A>
                        )
                    };
                })
            }
        });
    }, [valueIsNull, hotKeyWords, recentSearchWords, searchSug, loading]);


    return (
        <AutoComplete
            className={classNames(props.className, 'paddle-head-search-ac')}
            value={value}
            getPopupContainer={getPopupContainer}
            open={open}
            onDropdownVisibleChange={onOpen}
            listHeight={800}
            options={options}
            onChange={onChange}
            dropdownMatchSelectWidth={800}
            dropdownAlign={{ points: ['br', 'tr'], offset: [0, 1] }}
            onSelect={onSelect}
            notFoundContent={' '}
            dropdownRender={(menu) => (
                <div
                    className={classNames({
                        'paddle-head-search-ac-overlay': true,
                        'paddle-head-search-ac-overlay-type1': valueIsNull,
                        'paddle-head-search-ac-overlay-type2': !valueIsNull
                    })}
                >
                    {loading
                        ? (<div className="paddle-head-search-ac-overlay-loading-wrap">
                            <LoadingOutlined />
                        </div>)
                        : (<>
                            <div>
                                {menu}
                            </div>
                            {!valueIsNull
                                && (<>
                                    <div className="paddle-head-search-ac-overlay-divider" />
                                    <div className="paddle-head-search-ac-overlay-more">
                                        <A onClick={handleOnSubmitSearch}>查看更多关于“{value}”的结果</A>
                                    </div>
                                </>)
                            }
                        </>)}
                </div>
            )}
        >
            {(() => {
                if (props.children && isValidElement(props.children)) {
                    return React.cloneElement(
                        props.children,
                        {
                            prefix: <SearchOutlined />,
                            allowClear: open,
                            bordered: open,
                            onPressEnter: handleOnSubmitSearch,
                            placeholder: '请输入搜索关键词'
                        }
                    );
                }
                else if (props.children) {
                    return props.children;
                }

                return (
                    <Input
                        prefix={<SearchOutlined />}
                        allowClear={open}
                        bordered={open}
                        onPressEnter={handleOnSubmitSearch}
                        placeholder="请输入搜索关键词"
                    />
                )
            })()}
        </AutoComplete>
    );
}

export default SearchAC;
